import {
  saveBudget,
  useGetBudget,
  useListProperties,
  type BudgetOut,
  type BudgetRowOut,
} from '@myrentalmate/api-client';
import {
  Alert,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { DataTable, type DataColumn } from '../../components/DataTable';
import { okData, okItems } from '../../lib/apiData';
import { apiErrorMessage } from '../../lib/apiError';
import { formatMoney, titleCase, todayIso } from '../../lib/format';
import { useCan } from '../../lib/permissions';

function BudgetEditor({ budget, canManage }: { budget: BudgetOut; canManage: boolean }) {
  const queryClient = useQueryClient();
  const [amounts, setAmounts] = useState<Record<string, string>>(() =>
    Object.fromEntries(budget.rows.map((r) => [r.account_code, String(Number(r.amount))])),
  );
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await saveBudget({
        year: budget.year,
        property_id: budget.property_id,
        lines: budget.rows.map((r) => ({
          account_code: r.account_code,
          amount: Number(amounts[r.account_code] || 0).toFixed(2),
        })),
      });
      await queryClient.invalidateQueries();
      setSaved(true);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const columns: DataColumn<BudgetRowOut>[] = [
    { label: 'Code', render: (r) => r.account_code },
    { label: 'Account', render: (r) => r.account_name },
    { label: 'Type', render: (r) => titleCase(r.account_type) },
    {
      label: 'Budget for the Year',
      align: 'right',
      render: (r) =>
        canManage ? (
          <TextField
            size="small"
            type="number"
            value={amounts[r.account_code] ?? ''}
            slotProps={{
              htmlInput: {
                'aria-label': `Budget for ${r.account_name}`,
                min: 0,
                step: '0.01',
                style: { textAlign: 'right' },
              },
            }}
            sx={{ width: 140 }}
            onChange={(e) => setAmounts({ ...amounts, [r.account_code]: e.target.value })}
          />
        ) : (
          formatMoney(r.amount)
        ),
    },
  ];
  const income = (type: string) =>
    budget.rows
      .filter((r) => r.account_type === type)
      .reduce((total, r) => total + Number(amounts[r.account_code] || 0), 0)
      .toFixed(2);
  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}
      {saved && <Alert severity="success">Budget saved.</Alert>}
      <DataTable columns={columns} rows={budget.rows} rowKey={(r) => r.account_code} />
      <Stack direction="row" spacing={3} sx={{ alignItems: 'center' }}>
        <Typography>Income: {formatMoney(income('income'))}</Typography>
        <Typography>Expenses: {formatMoney(income('expense'))}</Typography>
        {canManage && (
          <Button variant="contained" disabled={busy} onClick={() => void save()}>
            Save budget
          </Button>
        )}
      </Stack>
    </Stack>
  );
}

export function BudgetsPage() {
  const canManage = useCan('accounting.manage');
  const [year, setYear] = useState(todayIso().slice(0, 4));
  const [propertyId, setPropertyId] = useState('');
  const properties = okItems(useListProperties({ page: 1, page_size: 200 }).data);
  const yearNumber = Number(year);
  const valid = yearNumber >= 2000 && yearNumber <= 2100;
  const query = useGetBudget(
    { year: yearNumber, ...(propertyId ? { property_id: Number(propertyId) } : {}) },
    { query: { enabled: valid } },
  );
  const budget = okData(query.data);
  return (
    <Stack spacing={2}>
      <div>
        <Typography variant="h5">Budgets</Typography>
        <Typography color="text.secondary">
          A yearly amount for each account, for the whole business or one property. Compare them
          with the Budget vs. Actual report.
        </Typography>
      </div>
      <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 2 }}>
        <TextField
          size="small"
          type="number"
          label="Year"
          value={year}
          sx={{ width: 110 }}
          onChange={(e) => setYear(e.target.value)}
        />
        <TextField
          select
          size="small"
          label="Budget For"
          value={propertyId}
          sx={{ minWidth: 220 }}
          slotProps={{ inputLabel: { shrink: true }, select: { displayEmpty: true } }}
          onChange={(e) => setPropertyId(e.target.value)}
        >
          <MenuItem value="">The Whole Business</MenuItem>
          {properties.map((p) => (
            <MenuItem key={p.id} value={String(p.id)}>
              {p.name}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      {query.isLoading && <CircularProgress aria-label="Loading" />}
      {budget && (
        <BudgetEditor
          key={`${budget.year}:${budget.property_id ?? 'all'}`}
          budget={budget}
          canManage={canManage}
        />
      )}
    </Stack>
  );
}
